import { AwareHttpRequestModule, AwareHttpService } from '@appbolaget/aware-http';
import { UntypedFormGroup, UntypedFormBuilder, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Component, Input, OnInit, inject } from '@angular/core';
import { MediaDialogService } from '../../media-dialog.service';
import { DialogService } from '@viewservices';
import { MediaService } from '../../media.service';
import { tap, debounceTime } from 'rxjs/operators';
import { environment } from '@env';
import { Bucket, File } from '@appbolaget/aware-model';
import { Store } from '@ngxs/store';
import { AwareAuthService } from '@appbolaget/aware-auth';
import { UnitState } from 'app/state/unit.state';
import { TranslateModule } from '@ngx-translate/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatButtonModule } from '@angular/material/button';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatDividerModule } from '@angular/material/divider';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatIconModule } from '@angular/material/icon';
import { NgIf, NgFor, AsyncPipe } from '@angular/common';
import { AwareSecurityIsGodDirective, AwareSecurityIsGrantedDirective, AwareSecurityService } from '@appbolaget/aware-security';
import { SecureImagePipe } from '@helpers/pipes';
import { FileDetailsPipe } from '@components/media/pipes/file-details.pipe';
import { MatMenuModule } from '@angular/material/menu';

@Component({
    selector: 'aw-file-details',
    templateUrl: 'file-details.component.html',
    styleUrls: ['./file-details.component.scss'],
    standalone: true,
    imports: [
        NgIf,
        MatIconModule,
        MatGridListModule,
        NgFor,
        MatDividerModule,
        AwareSecurityIsGrantedDirective,
        AwareSecurityIsGodDirective,
        FormsModule,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatSlideToggleModule,
        MatButtonModule,
        MatTooltipModule,
        TranslateModule,
        SecureImagePipe,
        FileDetailsPipe,
        AsyncPipe,
        MatMenuModule,
    ],
    providers: [SecureImagePipe, FileDetailsPipe],
})
export class FileDetailsComponent implements OnInit {
    _items: Array<File | Bucket>;

    files: any[];

    mediaForm: UntypedFormGroup;

    @Input('items')
    set items(items) {
        this._items = items;

        this.files = items.filter((item) => item instanceof File);

        if (items.length === 1) {
            this.buildForm();
        }

        this.CAN_RETURN_FILES =
            this.options && this.options.expectsValue ? this.mediaDialogService.canReturnSelectedFiles(this.files) : false;
    }

    get items(): Array<File | Bucket> {
        return this._items;
    }

    @Input()
    options;

    CAN_RETURN_FILES: boolean;

    private store = inject(Store);
    private awareAuth = inject(AwareAuthService);
    private secureImgPipe = inject(SecureImagePipe);
    private awareSecurityService = inject(AwareSecurityService);

    isGod$ = this.awareSecurityService.isGod();

    constructor(
        public mediaDialogService: MediaDialogService,
        private dialogService: DialogService,
        private mediaService: MediaService,
        private formBuilder: UntypedFormBuilder,
        private api: AwareHttpService,
    ) {}

    ngOnInit() {}

    copyFileLink(useAlias: boolean = false) {
        const path = (this.files[0] as File).getFilePath(null, useAlias);
        const url = this.secureImgPipe.transform(path, null, true, false);

        navigator.clipboard.writeText(url);
    }

    deleteSelectedItems() {
        this.dialogService.confirm('COMMON.ARE_YOU_SURE', 'MEDIA.CONFIRM_DELETE', true).then((toDelete) => {
            if (toDelete) {
                this.mediaService
                    .deleteItems(this.items)
                    .pipe(
                        tap(() => {
                            this.mediaService.refresh$.next();
                        }),
                    )
                    .subscribe();
            }
        });
    }

    download(forceDownload = false) {
        if (this.files[0]) {
            const token = this.awareAuth.token;
            const unit = this.store.selectSnapshot(UnitState.activeUnit);

            if (forceDownload) {
                window.open(
                    `${environment.api.url}/@media/filesystem/download/${this.files[0].uuid}?unit=${unit.uuid}&token=${token}`,
                    '_blank',
                );
            } else {
                window.open(this.secureImgPipe.transform(this.files[0].getFilePath(null, true)), '_blank');
            }
        }
    }

    async editTitle() {
        const item = this.items[0];

        const values = await this.dialogService.multiPrompt<{ label: string; value: string }[]>(
            'Redigera uppgifter',
            item instanceof File
                ? [
                      { label: 'Namn', value: item.title },
                      { label: 'Alt-beskrivning', value: item.alt },
                  ]
                : [{ label: 'Namn', value: item.title }],
            true,
        );
        if (values) {
            const [title, alt] = values.map((val) => val.value);
            console.log('title', title, 'alt', alt);
            item.title = title;
            if (item instanceof File) {
                item.alt = alt;
            }

            this.mediaService.save(item).subscribe();
        }

        // this.dialogService.prompt('MEDIA.CHANGE_NAME', 'MEDIA.NAME', item.title, true).then((result) => {
        //     if (result && result.length) {
        //         item.title = result;

        //         this.mediaService.changeTitle(item).subscribe();
        //     }
        // });
    }

    returnSelectedFiles() {
        if (this.mediaDialogService.canReturnSelectedFiles(this.files)) {
            this.mediaDialogService.returnFiles(this.files);
        }
    }

    private buildForm() {
        const item = this.items[0];
        let alias: any = item.alias ? item.alias.split(':') : null;

        if (alias && alias.length) {
            if (alias[0] === this.store.selectSnapshot(UnitState.activeUnit).uuid) {
                alias.shift();
            }

            alias = alias.join(':');
        }

        this.mediaForm = this.formBuilder.group({
            alias: [alias],
            public: item.public,
        });

        this.mediaForm.valueChanges
            .pipe(
                debounceTime(500),
                tap((values) => {
                    const unit = this.store.selectSnapshot(UnitState.activeUnit);
                    alias = `${unit.uuid}:${values.alias}`;

                    if (values.alias) {
                        item.alias = alias;
                    } else {
                        item.alias = null;
                    }

                    item.public = values.public;

                    item.save(this.api, { fields: ['alias', 'public'] })
                        .module(AwareHttpRequestModule.Media)
                        .execute()
                        .subscribe();
                }),
            )
            .subscribe();
    }
}

// let alias: any = this.navigation.alias ? this.navigation.alias.split(':') : null;
//         let dontPrefixAlias = false;

//         if (alias && alias.length) {
//             if (alias[0] === this.auth.getActiveUnit().uuid) {
//                 alias.shift();
//             } else {
//                 dontPrefixAlias = true;
//             }

//             alias = alias.join(':');
//         }

// const unit = this.auth.getActiveUnit();
// alias = `${unit.uuid}:${alias}`;
